/**
 * Domain models — kept framework agnostic so they can be shared between
 * Redux slices, RTK Query, components and reports.
 */

export type TaskStatus =
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'DISCUSSION'
  | 'BLOCKED';

export type TaskPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export type CallType = '4PM' | '8PM';

export interface Project {
  id: string;
  projectName: string;
  projectCode: string;
  description?: string;
  createdBy: string;
  createdDate: number; // epoch ms
}

export interface Sprint {
  id: string;
  projectId: string;
  sprintName: string;
  startDate: string; // ISO yyyy-MM-dd
  endDate: string;
  sprintGoal?: string;
  goalDescription?: string;
  createdDate: number;
}

export interface Task {
  id: string;
  projectId: string;
  sprintId: string;
  ticketNo: string;
  title: string;
  description?: string;
  assignee?: string;
  status: TaskStatus;
  priority: TaskPriority;
  storyPoints?: number;
  createdDate: number;
  updatedDate: number;
}

export interface Assignee {
  id: string;
  projectId: string;
  name: string;
  email?: string;
  color: string;
  avatarUrl?: string;
}

export interface DailyGoal {
  id: string;
  sprintId: string;
  assignee: string;
  goal: string;
  date: string; // ISO yyyy-MM-dd
  createdDate: number;
}

export interface CallTopic {
  id: string;
  sprintId: string;
  callType: CallType;
  topic: string;
  date: string;
  createdDate: number;
}

export interface Refinement {
  id: string;
  sprintId: string;
  topic: string;
  notes?: string;
  createdDate: number;
}

export interface ScopeItem {
  id: string;
  sprintId: string;
  content: string; // supports markdown / bullet text
  createdDate: number;
}

export const TASK_STATUS_META: Record<
  TaskStatus,
  { label: string; icon: string; color: string }
> = {
  IN_PROGRESS: { label: 'In Progress', icon: '🟡', color: '#f5a623' },
  COMPLETED: { label: 'Completed', icon: '✅', color: '#2e7d32' },
  DISCUSSION: { label: 'Discussion / Testing', icon: '🛑', color: '#1976d2' },
  BLOCKED: { label: 'Blocked / Hold', icon: '🚫', color: '#d32f2f' },
};

export const TASK_PRIORITY_META: Record<
  TaskPriority,
  { label: string; color: string }
> = {
  LOW: { label: 'Low', color: '#9e9e9e' },
  MEDIUM: { label: 'Medium', color: '#1976d2' },
  HIGH: { label: 'High', color: '#ed6c02' },
  CRITICAL: { label: 'Critical', color: '#d32f2f' },
};
